<template>
	<main>
		<div class="container c_inner">
			<section class="section_header">
				<div class="header_container">
					<p class="header_title">{{ getMustreadDetailByComputedData?.mustread?.mustread_title ?? '-' }}</p>
					<div class="header_sub">
						<div class="sub_info">
							<span class="user">{{ getMustreadDetailByComputedData?.mustread?.authority_group_name ?? '-' }}</span
							><span class="view">{{
								`조회수 ${getMustreadDetailByComputedData?.mustread?.mustread_hit ?? '-'}`
							}}</span>
							<span class="date">{{
								dayjs(getMustreadDetailByComputedData?.mustread?.mustread_enrollment_date).format('YYYY-MM-DD')
							}}</span>
						</div>
					</div>
				</div>
			</section>
			<section class="section_body">
				<div class="body_box">
					<div class="box_content c_editorContent">
						<div
							v-dompurify-html="getMustreadDetailByComputedData?.mustread?.mustread_content ?? '정보 불러오는 중'"
							class="content_body c_editorContent"
						></div>
					</div>
				</div>
			</section>
			<Comments :comments="commentData" :counts="commentCntData" :board="'notice'" @update:comment="updateComment" />
		</div>
	</main>
</template>

<script setup lang="ts">
// 내부 라이브러리
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
// 외부 라이브러리
import dayjs from 'dayjs';

import { CommentWithReplies, useGetCommentApiByComputed } from '@/api/comment';
// api
import { useGetMustreadDetailDataApiByComputed } from '@/api/notice';
// component
import Comments from '@/components/postDetail/Comments.vue';
// store
import { useHeaderStore } from '@/stores/header';
// util
import { handleLinkClick } from '@/utils/directive/editorLinkOpenNewTab';

// -----variable-----
const route = useRoute();
const router = useRouter();
const headerStore = useHeaderStore();

const commentData = ref<CommentWithReplies[]>([]);
const commentCntData = ref(0);
const contentBodies = ref<HTMLDivElement[]>([]);
// -----api-----
const { data: getMustreadDetailByComputedData, refetch: refetchGetMustreadDetail } =
	useGetMustreadDetailDataApiByComputed(
		computed(() => {
			return {
				mustread_pk: route.params.pk as string,
			};
		})
	);
const { data: getCommentByComputedData, refetch: refetchGetCommentByComputed } = useGetCommentApiByComputed(
	computed(() => {
		return {
			board: 'notice',
			pk: Number(route.params.pk),
		};
	})
);
// -----function-----
// 댓글 렌더링에 필요한 데이터 생성하는 함수
const createCommentData = () => {
	if (getCommentByComputedData.value) {
		commentData.value = [];
		Object.values(getCommentByComputedData.value.comment_data).forEach((item) => {
			commentData.value.push(item);
		});
		commentCntData.value = getCommentByComputedData.value.comment_data_count;
	}
};
// 댓글이 업데이트되면 실행되는 함수
const updateComment = async () => {
	await refetchGetCommentByComputed();
	createCommentData();
};

watch(
	() => headerStore.getCountry === 'USA',
	() => {
		const pk = route.params.pk as string;
		if (headerStore.getCountry === 'USA' && (pk === '21' || pk === '90')) {
			router.replace('/');
		}
	}
);

watch(
	() => route.params.pk,
	async () => {
		await refetchGetMustreadDetail();
		await refetchGetCommentByComputed();
	}
);

watch(
	() => getCommentByComputedData.value,
	() => {
		createCommentData();
	}
);
onMounted(() => {
	const pk = route.params.pk as string;
	if (pk === '21' || pk === '90') {
		headerStore.setCountry('KR');
	}
});
//editor 링크 클릭 시 새창 열기
// COMMENT : AOS에서는 target: _blank로 새창을 띄울 수 없어 추가된 코드입니다.
watch(
	[getMustreadDetailByComputedData],
	() => {
		contentBodies.value = Array.from(document.querySelectorAll('.c_editorContent')); // 모든 요소 선택

		contentBodies.value.forEach((contentBody) => {
			// 기존 리스너 제거 (필요한 경우)
			contentBody.removeEventListener('click', handleLinkClick);

			// 새로운 리스너 추가
			contentBody.addEventListener('click', handleLinkClick);
		});
	},
	{ immediate: true }
);

watch(
	() => route.params.pk,
	async () => {
		await refetchGetMustreadDetail();
		await refetchGetCommentByComputed();
	}
);
</script>

<style scoped lang="scss">
.container {
	display: flex;
	flex-direction: column;
	max-width: 68rem;
	padding-top: var(--header-height);
	margin: 7.2rem auto 8.8rem;
	@media screen and (max-width: 1280px) {
		margin: 4rem auto 8rem;
	}
}
.section_header {
	.header_container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		padding-bottom: 1.6rem;
		border-bottom: 1px solid #393d45;
		margin-bottom: 4rem;
		.header_title {
			font-size: var(--font-s-x-large);
			font-weight: var(--font-w-bold);
			line-height: 2.387rem;
		}
		.header_sub {
			display: flex;
			justify-content: space-between;
			font-size: var(--font-s-small);
			font-weight: var(--font-w-light);
			line-height: 1.551rem;
			color: #818393;
			.sub_info {
				display: flex;
				align-items: center;
				span {
					margin: 0 8px;
					position: relative;
				}
				span:first-child {
					margin-left: 0;
				}
				span:last-child::after {
					display: none; /* 마지막 span에는 border를 추가하지 않음 */
				}
				span::after {
					content: '';
					position: absolute;
					right: -8px;
					top: 50%;
					transform: translateY(-50%);
					height: 8px; /* border의 높이 */
					border-left: 1px solid #393d45; /* 원하는 border 색상으로 변경 */
				}
			}
		}
	}
}
.section_body {
	display: flex;
	flex-direction: column;
	gap: 2.4rem;
	margin-bottom: 8.8rem;
	.body_box {
		background-color: var(--color-bg-mid);
		border-radius: 10px;
		.box_content {
			padding: 2rem;
		}
	}
	.body_btn {
		display: flex;
		justify-content: end;
		gap: 0.8rem;
		button {
			font-size: var(--font-s-mid);
			font-weight: var(--font-w-semi-bold);
			line-height: 1.79rem;
			padding: 1.3rem 1.6rem;
			border-radius: 0.6rem;
		}
		.btn_delete {
			border: 1px solid #818393;
		}
		.btn_modify {
			background-color: #ff5e00;
		}
	}
}
.section_comment {
	.comment_header {
		margin-bottom: 1.6rem;
		.header_title {
			font-size: var(--font-s-x-large);
			font-weight: var(--font-w-bold);
			line-height: 2.387rem;
		}
	}
	.comment_inputArea {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		margin-bottom: 3.2rem;
		.inputArea_textArea {
			height: 16.4rem;
		}
		.inputArea_btnArea {
			width: 100%;
			display: flex;
			justify-content: end;
			button {
				font-size: var(--font-s-mid);
				font-weight: var(--font-w-semi-bold);
				line-height: 1.79rem;
				background-color: #ff5e00;
				padding: 1.3rem 1.6rem;
				border-radius: 0.6rem;
			}
		}
	}
}
</style>
